import { useCallback, useEffect, useState } from 'react';

const light_theme = {
  element_background: '#FFFFFF',
  border: '1px solid #e2e2e2',
  checkbox_color_active: '#276EF1',
  card_avatar: '#2B3648',
  navigation_background: '#2B3648',
  navigation_border: '1px solid #2B3648',
  background: '#F6F6F6',
  font_color: '#2b3648',
  font_color_inverse: '#fff',
  labels: {
    label_font_color: '#2b3648',
    label_font_weight: 500,
  },
  colors: {
    primary: '#2e3440',
    secondary: '#55a3ef',
    blue: '#276EF1',
  },
  globals: {
    primary_font: "'Roboto', sans-serif",
    secondary_font: "'Roboto', sans-serif",
    font_size: '13px',
    font_primary_color: '#2b3648',
    placeholder_color: '#636c72',
    border_color: '#E2E2E2',
  },
  modal: {
    modal_background: '#FFFFFF',
    modal_border: '0px solid #2e3440',
    modal_close_color: '#2b3648',
    modal_border_radius: '12px',
  },
  wizard: {
    wizard_border_radius: '12px',
  },
  inputs: {
    input_border: '1px solid #E2E2E2',
    input_border_focus: '1px solid #276ef1',
    input_font_color: '#2b3648',
    input_font_color_focus: '#2b3648',
    input_border_disabled: '1px solid #adbbd5',
    input_font_color_disabled: 'rgba(43, 54, 72, 0.8)',
    input_background: '#FFFFFF',
    input_font_color_placeholder: 'rgba(43, 54, 72, 0.8)',
    input_background_disabled: '#f2f3f4',
    input_font_color_hover: '#276ef1',
  },
  dropdowns: {
    dropdown_border: '1px solid #276ef1',
    dropdown_options_font_color: '#2b3648',
    dropdown_options_background_hover: '#e4edf9',
    dropdown_options_background_active: '#e4edf9',
  },
  switches: {
    switch_on_color: '#276ef1',
    label_font_weight: 400,
    label_font_color: '#fff',
    label_font_color_disabled: '#fff',
    label_font_color_active: '#fff',
  },
  tables: {
    table_border: '1px solid #E2E2E2',
    table_header_background: '#fff',
    table_header_font_color: '#2b3648',
    table_row_font_color: '#2b3648',
    table_header_font_size: '12px',
    table_header_font_weight: 500,
    table_header_font_color_hover: 'rgba(43, 54, 72, 0.8)',
  },
  calendar: {
    calendar_primary: '#276ef1',
    calendar_secondary: '#fff',
    calendar_font_size: '12px',
    calendar_font_color: '#2b3648',
    calendar_range_font_color: '#2b3648',
    calendar_background_active: '#276ef1',
    calendar_background: '#fff',
    calendar_border: '1px solid #E2E2E2',
  },
  tooltip: {
    tooltip_font_color: '#fff',
    tooltip_background: 'rgb(43, 54, 72)',
    tooltip_border: '1px solid rgb(43, 54, 72)',
  },
  actions: {
    action_background: '#276ef1',
    action_font_color: '#fff',
    action_border: '1px solid #276ef1',
    action_background_hover: '#276ef1',
    action_font_color_hover: '#fff',
    action_border_hover: '1px solid #276ef1',
    action_background_active: '#276ef1',
    action_font_color_active: '#fff',
    action_border_active: '1px solid #276ef1',
    action_font_size: '12px',
    action_font_weight: 500,
    action_border_radius: '4px',
    action_padding: '6px 12px',
    action_gap: '8px',
  },
  editor: {
    editor_theme: 'vs-dark',
    editor_background_minimap: '#1e1d1d',
    editor_background: '#1D1D1D',
    editor_background_selection: '#3d4350',
    editor_background_highlight: '#3d4350',
    editor_font_color_cursor: '#fff',
    editor_font_color_indent: '#fff',
    editor_toolbar_gap: '16px',
    editor_border: `1px solid #F6F6F6`,
    editor_border_radius: '6px',
    action_padding: '3px 10px',
  },
  toolbar: {
    action_padding: '7px 14px',
    action_background: '#f6f6f6',
    action_background_hover: '#276ef1',
    action_background_active: '#276ef1',
    action_border: '1px solid #e2e2e2',
    action_border_hover: '1px solid #276ef1',
    action_border_active: '1px solid #276ef1',
    action_font_color: 'rgba(0, 0, 0, 0.65)',
    action_font_color_hover: '#fff',
    tooltip_background: '#141414',
    tooltip_border: '1px solid #141414',
  },
  progress: {
    progress_background: '#f6f6f6',
    progress_bar_background: '#276ef1',
  },
  paper: {
    paper_border: '1px solid #e2e2e2',
    paper_background: '#fff',
  },
  snackbar: {
    snackbar_background: '#2B3648',
    snackbar_font_color_hover: '#276EF1',
  },
  error_background: 'rgba(255, 239, 237, 1)',
};

const dark_theme = {
  element_background: '#141414',
  border: '1px solid #414043',
  border_color: '#1D1D1D',
  checkbox_color_active: '#276ef1',

  card_avatar: '#fff',
  navigation_background: '#141414',
  navigation_border: '1px solid #414043',
  background: '#1D1D1D',
  font_color: '#fff',
  font_color_inverse: '#1D1D1D',
  labels: {
    label_font_color: '#fff',
    label_font_weight: 500,
  },
  inputs: {
    input_border: '1px solid #414043',
    input_border_focus: '1px solid #276ef1',
    input_font_color: '#fff',
    input_font_color_disabled: 'rgba(255, 255, 255, 0.8)',
    input_background: '#1D1D1D',
    input_font_color_placeholder: 'rgba(255, 255, 255, 0.8)',
    input_background_disabled: '#414043',
    input_border_disabled: '1px solid #555e6f',
    input_font_color_focus: '#276EF1',
    input_font_color_hover: '#276EF1',
  },
  dropdowns: {
    dropdown_border: '1px solid #414043',
    dropdown_options_font_color: '#fff',
    dropdown_options_font_color_active: '#276ef1',
    dropdown_options_font_color_hover: '#276ef1',
    dropdown_options_background_hover: '#414043',
    dropdown_options_background_active: '#1D1D1D',
  },
  colors: {
    primary: '#2e3440',
    secondary: '#55a3ef',
    blue: '#276EF1',
  },
  globals: {
    primary_font: "'Roboto', sans-serif",
    secondary_font: "'Roboto', sans-serif",
    monospace_font: '',
    font_size: '13px',
    font_primary_color: '#fff',
    border_color: '#414043',
    placeholder_color: '#636c72',
    active_color: '#9279f4',
  },
  modal: {
    modal_background: '#141414',
    modal_border: '1px solid #414043',
    modal_close_color: '#fff',
    modal_border_radius: '12px',
  },
  wizard: {
    wizard_border_radius: '12px',
  },
  switches: {
    switch_on_color: '#276ef1',
    label_font_weight: 400,
    label_font_color: '#fff',
    label_font_color_disabled: '#fff',
    label_font_color_active: '#fff',
  },
  tables: {
    table_header_background: '#141414',
    table_row_background: '#141414',
    table_border: '1px solid #414043',
    table_row_background_hover: '#414043',
    table_header_font_color: '#fff',
    table_row_font_color: '#fff',
    table_header_font_size: '12px',
    table_header_font_weight: 500,
    table_header_font_color_hover: '#e2e2e2',
  },
  calendar: {
    calendar_primary: '#276ef1',
    calendar_secondary: '#141414',
    calendar_font_size: '12px',
    calendar_font_color: '#fff',
    calendar_background: '#141414',
    calendar_range_font_color: '#fff',
    calendar_border: '1px solid #414043',
  },
  tooltip: {
    tooltip_background: '#276EF1',
    tooltip_border: '1px solid #276EF1',
    tooltip_font_color: '#fff',
  },
  toolbar: {
    action_padding: '7px 14px',
    action_background: 'rgb(29, 29, 29)',
    action_background_hover: '#414043',
    action_background_active: '#276ef1',
    action_border: '1px solid #414043',
    action_border_hover: '1px solid #414043',
    action_border_active: '1px solid #276ef1',
    action_font_color: '#fff',
    action_font_color_hover: '#fff',
    tooltip_background: '#276EF1',
    tooltip_border: '1px solid #276EF1',
    tooltip_font_color: '#fff',
  },
  actions: {
    action_background: '#276ef1',
    action_font_color: '#fff',
    action_border: '1px solid #276ef1',
    action_background_hover: '#276ef1',
    action_font_color_hover: '#fff',
    action_border_hover: '1px solid #276ef1',
    action_background_active: '#276ef1',
    action_font_color_active: '#fff',
    action_border_active: '1px solid #276ef1',
    action_font_size: '12px',
    action_font_weight: 500,
    action_border_radius: '4px',
    action_padding: '6px 12px',
    action_gap: '8px',
  },
  editor: {
    editor_theme: 'vs-dark',
    editor_background_minimap: '#1e1d1d',
    editor_background: '#1D1D1D',
    editor_background_selection: '#3d4350',
    editor_background_highlight: '#3d4350',
    editor_font_color_cursor: '#fff',
    editor_font_color_indent: '#fff',
    editor_toolbar_gap: '16px',
    editor_border: `1px solid #414043`,
    editor_border_radius: '6px',
    action_padding: '3px 10px',
  },
  progress: {
    progress_background: '#1d1d1d',
    progress_bar_background: '#276ef1',
  },
  snackbar: {
    snackbar_background: '#2B3648',
    snackbar_font_color_hover: '#276EF1',
  },
  paper: {
    paper_background: 'rgb(20, 20, 20)',
  },
  error_background: 'rgb(54, 48, 47)',
};

function is_dark_theme() {
  let settings = null;
  const stored_settings = localStorage.getItem('settings');
  if (stored_settings) {
    settings = JSON.parse(stored_settings);
  }
  return settings && settings.is_dark_theme;
}

export const useSwitchTheme = () => {
  const [theme, setTheme] = useState(is_dark_theme() !== null ? is_dark_theme() : false);

  const setLocalStorageTheme = useCallback((theme_is: boolean) => {
    localStorage.setItem('settings', JSON.stringify({ is_dark_theme: theme_is }));
    window.dispatchEvent(new Event('storage'));
  }, []);

  const handleChangeStorage = useCallback(() => {
    setTheme(is_dark_theme());
  }, []);

  useEffect(() => {
    window.addEventListener('storage', handleChangeStorage);
    return () => window.removeEventListener('storage', handleChangeStorage);
  }, [handleChangeStorage]);

  return { theme: theme ? dark_theme : light_theme, is_dark_theme: theme, setLocalStorageTheme };
};
